/* Sticky cart footer — cart PAGE, mobile only (<= 749px).
   ---------------------------------------------------------------------------
   The real `.cart__footer` is repositioned rather than duplicated: it flips to
   `position: fixed` whenever its natural in-flow slot is off screen — below the
   fold OR scrolled past above — and drops back into the page when the slot is
   fully visible. Nothing is cloned, so there is still exactly one subtotal, one
   <cart-discount-form> and one `cart-discount-input` id on the page.

   The wrapping <sticky-cart-footer> is the placeholder: while the footer is
   fixed the wrapper holds an explicit height equal to the panel's, so the
   document never reflows on the flip and the hand-off at the bottom edge is
   pixel-identical (cart.css keeps the safe-area padding in BOTH states so the
   two heights match on devices with a home indicator).

   Custom element rather than a DOMContentLoaded script because cart-drawer.js
   replaceWith()s the whole [data-cart-root] after every cart mutation — the
   element re-upgrades itself, same as <cart-discount-form>. */

const MQ = '(max-width: 749px)';
const HEIGHT_VAR = '--cart-sticky-footer-h';
const BODY_CLASS = 'cart-sticky-footer-up';
/* Set once while a footer exists, never toggled on scroll — it drives the page's
   bottom padding, and a value that changed mid-scroll would move the hand-off
   point under the user. */
const ACTIVE_CLASS = 'cart-has-sticky-footer';
const GORGIAS_GAP = 0;

/* Gorgias injects its launcher ~5s after window "load" (see
   snippets/gorgias-facade.liquid); which iframe class it uses depends on the
   bundle version, hence both selectors.

   #gorgias-chat-container is deliberately NOT in this list. That wrapper is
   `position: static` (css--combined-styles-global.css has to add
   `position: relative` before a z-index will stick to it) while the launcher
   iframe inside it is `position: fixed` — so transforming the container makes it
   the containing block for that iframe, whose bottom/right then resolve against
   a static wrapper at the end of the document flow instead of the viewport. The
   launcher vanishes off screen. Move the iframes themselves; they hold their own
   fixed positioning and z-index (…global.css sets z-index: 1000 on them). */
const GORGIAS_SELECTORS = [
  'iframe[class*="gorgias-chat-key-"]',
  'iframe#chat-button',
];

/* Module scope so it survives the [data-cart-root] swap: the replacement
   element's connectedCallback runs synchronously inside replaceWith(), before
   paint, so restoring this stops the panel flashing back to the
   server-rendered default while the user is mid-scroll. */
let lastStuck = null;

/* A/B TEST — the resolved arm, cached here for the same reason. This panel is
   arm B only (see snippets/ig-cart-test.liquid), and the arm is a client-side
   key, so the very first instance may have to await window.IGCart.ready. Every
   instance after it reads the cached value SYNCHRONOUSLY, which is what keeps
   the replaceWith() hand-off flash-free on re-render. */
let igArm = null;

/* Original inline transforms, keyed by element, so release puts back exactly
   what Gorgias had rather than blindly clearing a property it may be animating. */
const gorgiasOriginals = new WeakMap();

/* Every launcher/window iframe Gorgias has rendered so far. They are siblings,
   never nested, so shifting all of them can't compound the offset — and a
   viewport-anchored iframe is the only thing that needs to move. */
function gorgiasTargets() {
  const found = [];
  for (const selector of GORGIAS_SELECTORS) {
    for (const el of document.querySelectorAll(selector)) {
      // `bottom`-anchored only; a statically-positioned node isn't the launcher.
      const position = getComputedStyle(el).position;
      if (position === 'fixed' || position === 'absolute') found.push(el);
    }
  }
  return found;
}

/* !important because Gorgias styles its launcher inline. Fully guarded: a
   failure here must never break the cart. */
function moveGorgias(offset) {
  try {
    for (const el of gorgiasTargets()) {
      if (!gorgiasOriginals.has(el)) gorgiasOriginals.set(el, el.style.transform || '');

      if (offset > 0) {
        el.style.setProperty('transform', `translateY(-${offset}px)`, 'important');
      } else {
        const original = gorgiasOriginals.get(el);
        if (original) el.style.setProperty('transform', original);
        else el.style.removeProperty('transform');
      }
    }
  } catch (e) {
    /* no-op */
  }
}

class StickyCartFooter extends HTMLElement {
  #footer = null;
  #details = null;
  #mql = null;
  #ro = null;
  #mo = null;
  #frame = 0;
  #stuck = false;
  #panelHeight = 0;
  #discountOpen = false;
  /* False in arm A, or when the element was disconnected before the arm
     resolved — disconnectedCallback must not undo work that never happened. */
  #active = false;

  #onScroll = () => this.#schedule();
  /* Resize and breakpoint changes can both change the footer's own height (the
     mobile rules add safe-area padding; 1024px+ moves it into a grid column),
     so those paths re-measure where a plain scroll doesn't. */
  #onResize = () => {
    this.#measure();
    this.#schedule();
  };
  #onMedia = () => {
    this.#measure();
    this.#update(true);
  };
  #onToggle = () => this.#handleDiscountToggle();

  connectedCallback() {
    const IG = window.IGCart;
    // Test switched off, or the head payload never ran: behave as if this
    // element were not here at all. cart.css already leaves it inert.
    if (!IG || !IG.enabled) return;

    if (igArm !== null) {
      if (igArm === 'b') this.#activate();
      return;
    }

    IG.ready.then((arm) => {
      igArm = arm;
      // The cart can re-render while the promise is in flight; the replacement
      // instance will have run its own connectedCallback by then.
      if (arm === 'b' && this.isConnected) this.#activate();
    });
  }

  #activate() {
    this.#active = true;
    this.#footer = this.querySelector('.cart__footer');
    // Empty cart renders no footer at all — nothing to stick.
    if (!this.#footer) return;

    this.#details = this.querySelector('.cart-discount__details');
    this.#discountOpen = !!(this.#details && this.#details.open);
    this.#mql = window.matchMedia(MQ);
    this.#stuck = this.hasAttribute('data-stuck');
    document.body.classList.add(ACTIVE_CLASS);

    this.#measure();
    // Default to stuck (matches the server-rendered attribute) unless a prior
    // instance left a different state behind.
    this.#apply(this.#mql.matches && (lastStuck === null ? true : lastStuck));
    this.#update(true);

    window.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    // Safari < 14 only has the deprecated listener API.
    if (this.#mql.addEventListener) this.#mql.addEventListener('change', this.#onMedia);
    else if (this.#mql.addListener) this.#mql.addListener(this.#onMedia);

    // `toggle` does not bubble, so it is bound on the <details> itself.
    if (this.#details) this.#details.addEventListener('toggle', this.#onToggle);

    /* The panel's height moves with the item-count string, translated labels
       and applied discount pills — re-measure rather than assume. */
    if ('ResizeObserver' in window) {
      this.#ro = new ResizeObserver(() => {
        this.#measure();
        if (this.#stuck) this.#applyHeight();
        this.#schedule();
      });
      this.#ro.observe(this.#footer);

      /* …and the document, because the slot's POSITION moves whenever anything
         above it grows, which no scroll or resize event reports. On first load
         the page is still short — cart-recommendations.js injects the FBT
         carousel over the network, lazy images settle, fonts swap — so the
         first evaluation legitimately finds the footer on screen and releases
         it, and without this nothing re-checks until the user scrolls. */
      this.#ro.observe(document.documentElement);
    }

    /* Backstop for the same problem where ResizeObserver is unavailable, and for
       any late shift it doesn't surface. */
    if (document.readyState !== 'complete') {
      window.addEventListener('load', this.#onResize, { once: true });
    }

    this.#watchGorgias();
  }

  /* Gorgias appends its launcher several seconds after load. Without this, a
     user sitting still with the panel already up gets an un-shifted launcher
     over the CTA until they next scroll. `subtree` because the iframes land
     inside #gorgias-chat-container, not directly on <body>. */
  #watchGorgias() {
    if (gorgiasTargets().length || !('MutationObserver' in window)) return;
    this.#mo = new MutationObserver(() => {
      if (!gorgiasTargets().length) return;
      this.#mo.disconnect();
      this.#mo = null;
      moveGorgias(this.#stuck ? this.#panelHeight + GORGIAS_GAP : 0);
    });
    this.#mo.observe(document.body, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    /* Arm A, or the arm resolved after this instance was already gone: nothing
       was ever attached, and clearing the shared body classes / Gorgias offset
       here would stomp on whichever instance IS live. */
    if (!this.#active) return;

    window.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    window.removeEventListener('load', this.#onResize);

    if (this.#mql) {
      if (this.#mql.removeEventListener) this.#mql.removeEventListener('change', this.#onMedia);
      else if (this.#mql.removeListener) this.#mql.removeListener(this.#onMedia);
    }
    if (this.#details) this.#details.removeEventListener('toggle', this.#onToggle);
    if (this.#ro) this.#ro.disconnect();
    if (this.#mo) this.#mo.disconnect();
    this.#mo = null;
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = 0;

    /* A re-render disconnects this instance and connects the replacement in the
       same tick, so clearing the global side effects here is safe — the new
       element re-applies them before paint. */
    lastStuck = this.#stuck;
    document.body.classList.remove(BODY_CLASS);
    document.body.classList.remove(ACTIVE_CLASS);
    moveGorgias(0);
  }

  #schedule() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#update();
    });
  }

  /* Measured from the footer itself, in whichever state it is currently in —
     cart.css keeps the padding identical fixed vs inline, so both readings give
     the panel height. Not clamped to the viewport: if the footer ever exceeds
     it, CSS caps the panel with `max-height` + internal scroll and the extra
     reserved space is harmless whitespace at the end of the page. */
  #measure() {
    const height = Math.round(this.#footer.getBoundingClientRect().height);
    if (height <= 0 || height === this.#panelHeight) return;
    this.#panelHeight = height;
    /* Guarded by the equality check above: this property feeds the page's bottom
       padding, so writing it invalidates and relayouts <main>. #measure() runs on
       every resize (iOS URL-bar collapse included) and on every document resize,
       so re-writing an unchanged value would be pure churn — and would keep the
       documentElement observer below re-triggering itself. */
    document.documentElement.style.setProperty(HEIGHT_VAR, `${height}px`);
  }

  /* min-height as well as height: the stylesheet carries a 164px pre-JS
     reservation which would otherwise win over a measured panel shorter than
     that, and the reserved slot has to equal the panel exactly or the footer
     lands with a gap under it at the hand-off. */
  #applyHeight() {
    const value = this.#stuck && this.#panelHeight ? `${this.#panelHeight}px` : '';
    this.style.height = value;
    this.style.minHeight = value;
  }

  #apply(stuck) {
    this.#stuck = stuck;
    this.toggleAttribute('data-stuck', stuck);
    this.#applyHeight();
    document.body.classList.toggle(BODY_CLASS, stuck);
    moveGorgias(stuck ? this.#panelHeight + GORGIAS_GAP : 0);
    lastStuck = stuck;
  }

  #update(force) {
    if (!this.#footer || !this.#mql) return;

    let stuck;
    if (!this.#mql.matches || this.#discountOpen) {
      stuck = false;
    } else {
      /* The wrapper's box is the footer's resting slot in both states (explicit
         height while fixed, the footer's own height while inline), so this
         reading can't oscillate. Fully visible → the CTA is on screen, no panel
         needed; anything else → panel. `top < 0` is the "scrolled past" case. */
      const rect = this.getBoundingClientRect();
      stuck = !(rect.top >= 0 && rect.bottom <= window.innerHeight);
    }

    if (stuck !== this.#stuck || force) this.#apply(stuck);
  }

  /* Opening the discount disclosure inside a bottom-fixed panel puts its input
     under the iOS keyboard. Releasing the footer into normal document flow and
     scrolling it into view sidesteps that entirely. */
  #handleDiscountToggle() {
    this.#discountOpen = !!(this.#details && this.#details.open);
    this.#update(true);

    if (this.#discountOpen && this.#mql.matches) {
      try {
        this.scrollIntoView({ block: 'end', behavior: 'smooth' });
      } catch (e) {
        this.scrollIntoView(false);
      }
    }
  }
}

if (!customElements.get('sticky-cart-footer')) {
  customElements.define('sticky-cart-footer', StickyCartFooter);
}
